import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import styled from "@mui/material/styles/styled";
import {
  DataInput,
  DataInputPanel,
  mergeResponseToQF,
} from "../../dataInputTypes";
import { Suggestion } from "../hooks/useSuggestions";
import { RosebudColorMap } from "../rosebud/rosebudColorMap";
import { MobileView } from "../../../util/aKaunGeneralUtil";
import { Button, Theme } from "@mui/material";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";
import AddIcon from "@mui/icons-material/Add";
import { PatientCaseChange, PatientCaseOp } from "../usePatientCase";
import { QualifiedFeature, QualifiedFeatureFragment } from "fe-common/graphql";
import { PatientCaseGrouping } from "../../../context/diagnosticAgent";
import { PlanItem } from "./planItem";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { useScreenSize } from "../util/useScreenSize";
import { useDiagnosticNavigation } from "../useDiagnosticNavigation";
import { ClinicalReasoningIconDesktop } from "../common/clinicalReasoningIconDesktop";
import { KahunPanelSubGroup } from "shared";
import { DesktopOnly } from "../util/desktopOnly";
import { useChangePatientCase } from "../editPatient/useChangePatientCase";
import { AgentRoute } from "../util/agentRoutes";
import { useRouteHelper } from "../useRouteHelper";
import { MobileOnly } from "../util/mobileOnly";
import { ClinicalReasoningIconMobile } from "../common/clinicalReasoningIconMobile";
import { ClinicalReasoningTooltip } from "../common/ClinicalReasoningTooltip";

//region [[ Styles ]]

const PanelPlanItemView = styled("div")(
  ({ theme, hideoutline }: { theme?: any; hideoutline: string }) => ({
    display: "flex",
    flexDirection: "column",
    minHeight: "50px",
    [theme.breakpoints.down(MobileView)]: {
      paddingLeft: "0px",
      background: "#FFFFFF",
      boxShadow:
        hideoutline === "true" ? "unset" : "0px 1px 8px rgba(0, 0, 0, 0.12)",
      padding: "0px",
    },
    width: "100%",
  }),
);

const PanelName = styled("div")({
  fontSize: "14px",
});

const PanelItem = styled("div")(
  ({ theme, hideoutline }: { theme?: any; hideoutline: string }) => ({
    display: "flex",
    paddingTop: hideoutline === "true" ? "0px" : "15px",
    paddingBottom: hideoutline === "true" ? "0px" : "15px",
    flexDirection: "row",
    justifyContent: "space-between",
    flexWrap: "wrap",
    cursor: "pointer",
  }),
);

const LabTest = styled(PlanItem)(
  ({
    theme,
    indented,
    hideoutline,
  }: {
    theme?: Theme;
    indented: "true" | "false";
    hideoutline: string;
  }) => ({
    minHeight: indented === "true" ? "50px" : " auto",
    paddingLeft: indented === "true" ? "10px" : "0px",
    paddingRight: "0px",
    paddingTop: hideoutline === "true" ? "0px" : "auto",
    paddingBottom: hideoutline === "true" ? "0px" : "auto",
    flexWrap: "wrap",
    "& .refine-panel": {
      minHeight: "auto",
      padding: "2px 16px",
      "& .relation-name": {
        fontSize: "13px",
      },
    },
    "& .refine-button": { padding: "2px 16px" },
    [theme.breakpoints.down(MobileView)]: {
      "& .finding-title": {
        fontSize: "14px",
        margin: 0,
      },
    },
    [theme.breakpoints.up(MobileView)]: {
      width: "100%",
      overflow: "hidden",
      "& .finding-icon": {
        display: "none",
      },
      "& .finding-title": {
        fontSize: indented === "true" ? "14px" : "14px",
        margin: 0,
      },
    },
  }),
);

const LeftArea = styled("div")({
  display: "flex",
  marginLeft: "10px",
  alignItems: "center",
  flex: 2,
});

const RightArea = styled("div")({
  display: "flex",
  marginRight: "10px",
  alignItems: "center",
  flex: 1,
  justifyContent: "flex-end", // For when its being wraped, make it stick to right
});

const LabTestContainer = styled("div")(
  ({
    theme,
    islast,
    isonly,
    hideoutline,
  }: {
    theme?: any;
    islast: string;
    isonly: string;
    hideoutline: string;
  }) => ({
    [theme.breakpoints.up(MobileView)]: {
      borderTop: "unset",
      paddingTop: isonly === "true" ? "15px" : "0px !important",
      boxShadow: "unset",
    },

    [theme.breakpoints.down(MobileView)]: {
      boxShadow:
        isonly === "true" || hideoutline === "true"
          ? "unset"
          : "0px 1px 8px rgba(0, 0, 0, 0.12)",
      borderTop:
        isonly === "true" || hideoutline === "true"
          ? "unset"
          : "1px solid " + KahunColorMap.AccentLight,
    },
    display: "flex",
    marginTop: "1px",
    paddingLeft: "10px",
    paddingRight: "10px",
    borderBottomLeftRadius: islast === "true" ? "9px" : "0px",
    borderBottomRightRadius: islast === "true" ? "9px" : "0px",
    alignItems: "center",
  }),
);

const StyledAddIcon = styled(AddIcon)({
  fontSize: "18px",
  marginRight: "2px",
});

const ButtonAddIcon = styled(AddIcon)({
  fontSize: "16px",
  marginRight: "5px",
  color: "white",
  marginLeft: "-10px",
});

const ResultsButton = styled(Button)(({ theme }) => ({
  display: "flex",
  paddingTop: "1px",
  paddingBottom: "1px",
  paddingLeft: "8px",
  minWidth: "78px",
  alignItems: "center",
  textTransform: "capitalize",
  boxShadow: "unset",
  backgroundColor: KahunColorMap.Primary,
  color: "white",
  margin: "0 2px",
  "&:hover": {
    backgroundColor: KahunColorMap.Primary,
  },
  borderRadius: "36px",

  [theme.breakpoints.down(MobileView)]: {
    maxHeight: "27px",
    fontSize: "12px",
  },
}));

export const PanelClinicalReasoning = styled(ClinicalReasoningTooltip)(
  ({ theme }) => ({
    [theme.breakpoints.down(MobileView)]: {
      padding: "0px",
      marginTop: "10px",
      marginLeft: "20px",
      marginRight: "20px",
    },
  }),
);

const AddToPatientButton = styled(Button)(({ theme }) => ({
  color: "white",
  backgroundColor: KahunColorMap.Primary,
  textTransform: "capitalize",
  borderRadius: "20px",
  width: "90px",
  height: "30px",
  fontSize: "13px",
  boxShadow: "unset",

  "&:focus, &:hover, &:active": {
    backgroundColor: KahunColorMap.Primary,
    boxShadow: "unset",
  },

  [theme.breakpoints.down(MobileView)]: {
    fontSize: "13px",
    marginRight: "0px",
  },
}));

const SectionNumber = styled("div")(({ theme }) => ({
  backgroundColor: "transparent",
  color: RosebudColorMap.Default,
  fontWeight: 500,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%",
  fontSize: "12px",
  width: "30px",
  padding: "10px",
  marginRight: "5px",
  height: "30px",

  [theme.breakpoints.down(MobileView)]: {
    fontSize: "14px",
    marginRight: "0px",
  },
}));

//endregion [[ Styles ]]

//region [[ Props ]]

export interface PanelPlanItemProps {
  className?: string;
  panel: DataInputPanel;
  suggestions: Suggestion[];
  index: number;
  onChange: (change: PatientCaseChange) => void;
  hideOutline?: boolean;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

// AVI G - I suggest calling it PanelItem i find panelPlan very confusing
export const PanelPlanItem = ({ onChange, ...props }: PanelPlanItemProps) => {
  const [expand, setExpand] = useState(false);
  const [editable, setEditable] = useState(false);
  const { isMobile } = useScreenSize();
  const [showClinicalReasoning, setShowClinicalReasoning] = useState(false);

  const { navigateTo } = useRouteHelper();

  const isProcedure =
    props.panel &&
    props.panel.panelSubgroup.id === KahunPanelSubGroup.Procedures;

  const [panelSuggestions, setPanelSuggestions] = useState(
    props.suggestions.map((s) => ({
      ...s,
      finding: { ...s.finding, qualifiers: [] } as QualifiedFeatureFragment,
    })),
  );

  useEffect(() => {
    if (!isMobile) {
      setPanelSuggestions(
        props.suggestions.map((s) => ({
          ...s,
          finding: { ...s.finding, qualifiers: [] },
        })),
      );
    }
  }, [props.suggestions, isMobile]);

  const { setEditPanelPopupProps } = useDiagnosticNavigation();

  const { getCaseValue, getCaseValuePending } = useChangePatientCase();

  useEffect(() => {
    setExpand(!props.panel);
  }, [props.panel]);

  const onAddResults = useCallback(
    (event) => {
      event.stopPropagation();
      setEditable(!editable);
    },
    [setEditable, editable],
  );

  const onItemChangeEvent = useCallback(
    (resp: DataInput, qf: QualifiedFeature) => {
      if (resp) {
        const level = resp?.level?.toUpperCase();
        const negate = resp.negate === false ? "YES" : "NO";
        const eventLabel =
          (level ? level : negate) +
          " " +
          qf.name +
          (qf.qualifiers.length > 0
            ? qf.qualifiers.map((q) => q.value.name).join(",")
            : "");

        evt({
          category: CATEGORY.PLAN,
          action: ACTION.ADD_FINDING_TO_PLAN,
          label: eventLabel,
          fb: true,
        });
      } else {
        evt({
          category: CATEGORY.PLAN,
          action: ACTION.REMOVE_FINDING_FROM_PLAN,
          label: qf.name,
        });
      }
    },
    [],
  );

  const onItemChange = useCallback(
    (resp: DataInput, qf: QualifiedFeature) => {
      const index = panelSuggestions.findIndex(
        (suggestion) => suggestion.finding.id === qf.id,
      );

      if (resp) {
        const updatedFinding = mergeResponseToQF(qf, resp);
        const newSuggestion = {
          ...panelSuggestions[index],
          finding: updatedFinding,
        };
        setPanelSuggestions([
          ...panelSuggestions.slice(0, index),
          newSuggestion,
          ...panelSuggestions.slice(index + 1),
        ]);
        onChange({
          op: PatientCaseOp.replaceQFInSection,
          qf: updatedFinding,
          section: PatientCaseGrouping.WORKUP,
        });
      } else {
        const newSuggestion = {
          ...panelSuggestions[index],
          finding: { ...qf, qualifiers: [], negate: null },
        };

        setPanelSuggestions([
          ...panelSuggestions.slice(0, index),
          newSuggestion,
          ...panelSuggestions.slice(index + 1),
        ]);

        onChange({
          op: PatientCaseOp.deleteFromSection,
          qf: qf,
          section: PatientCaseGrouping.WORKUP,
        });
      }

      onItemChangeEvent(resp, qf);
    },
    [panelSuggestions, onChange, setPanelSuggestions, onItemChangeEvent],
  );

  const hideOutline = props.hideOutline ? "true" : "false";

  const resultsButton = useMemo(() => {
    if (props.panel || editable) return null;
    return (
      <ResultsButton
        className={"results-button"}
        variant="contained"
        onClick={onAddResults}
      >
        <StyledAddIcon />
        Results
      </ResultsButton>
    );
  }, [props.panel, editable, onAddResults]);

  const createOnChangeHandler = useCallback(
    (suggestion: Suggestion) => (resp: DataInput) => {
      onItemChange(resp, suggestion.finding);
    },
    [onItemChange],
  );

  const getSelectionValue = (suggestion: Suggestion) => {
    const getPatientCaseValue = isMobile ? getCaseValue : getCaseValuePending;
    return getPatientCaseValue(suggestion.finding, PatientCaseGrouping.WORKUP);
  };

  const addToPatient = () => {
    if (isMobile) {
      navigateTo({ route: AgentRoute.EditPanel + "/" + props.panel.id });
    } else {
      setEditPanelPopupProps({
        open: true,
        patientCaseGrouping: PatientCaseGrouping.CLINICAL_FEATURES,
        suggestions: panelSuggestions,
        panelContext: {
          panel: props.panel,
          caseGrouping: PatientCaseGrouping.WORKUP,
          panelCategory: props.panel
            ? props.panel.panelSubgroup
            : props.suggestions[0].finding.feature.category,
        },
      });
    }
  };

  const onShowClinicalReasoning = () => {
    if (!isMobile) return;
    evt({
      category: CATEGORY.PLAN,
      action: showClinicalReasoning
        ? ACTION.HIDE_CLINICAL_REASONING
        : ACTION.SHOW_CLINICAL_REASONING,
      label: props.panel.title,
    });

    setShowClinicalReasoning(!showClinicalReasoning);
  };

  return (
    <PanelPlanItemView className={props.className} hideoutline={hideOutline}>
      {props.panel && (
        <PanelItem className={"panel-name"} hideoutline={hideOutline}>
          <LeftArea className={"left-area"}>
            <SectionNumber>{props.index}</SectionNumber>
            <PanelName>
              {props.panel ? props.panel.title : "No Panel"}
            </PanelName>

            {isProcedure && (
              <>
                <DesktopOnly>
                  <ClinicalReasoningIconDesktop
                    suggestion={panelSuggestions[0]}
                  />
                </DesktopOnly>
                <MobileOnly>
                  <ClinicalReasoningIconMobile
                    showClinicalReasoning={showClinicalReasoning}
                    onShowClinicalReasoning={onShowClinicalReasoning}
                  />
                </MobileOnly>
              </>
            )}
          </LeftArea>
          <RightArea className={"right-area"}>
            <AddToPatientButton onClick={addToPatient} variant={"contained"}>
              <ButtonAddIcon />
              Results
            </AddToPatientButton>
          </RightArea>

          {showClinicalReasoning && (
            <PanelClinicalReasoning
              show={showClinicalReasoning}
              suggestion={panelSuggestions[0]}
            />
          )}
        </PanelItem>
      )}

      {expand &&
        panelSuggestions.map((suggestion, index) => {
          const labTestIndex =
            !props.panel && props.suggestions.length === 1 ? props.index : null;
          const isLast = (index === props.suggestions.length - 1).toString();
          const isOnly = (
            !props.panel && props.suggestions.length === 1
          ).toString();
          return (
            <LabTestContainer
              hideoutline={hideOutline}
              className="lab-test-container"
              key={index}
              islast={isLast}
              isonly={isOnly}
            >
              <LabTest
                className={"lab-test"}
                indented={props.panel !== null ? "true" : "false"}
                suggestion={suggestion}
                selectionValue={getSelectionValue(suggestion)}
                index={labTestIndex}
                showActions={false}
                hideSelectComponent
                showInlineReasoning
                editable={editable}
                panel={props.panel}
                hideoutline={hideOutline}
                onChange={createOnChangeHandler(suggestion)}
                externalButton={resultsButton}
              />
            </LabTestContainer>
          );
        })}
    </PanelPlanItemView>
  );
};
